/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { Box, Flex } from '@mantine/core';

// UI Local components
import {
	DonutChartContributions,
	KpiSection,
	TableLastContributions,
} from '@components/v2/ui';

// Mocks
import { ROLES } from '@config/constants/roles';
import { useLatestOffsetsQuery } from '@features/dashboard/api/get-latest-offsets';
import { useCreateCertificate } from '@features/offsets/api/create-certificate';
import { getCertificate } from '@features/offsets/api/get-certificate';
import { notifications } from '@mantine/notifications';
import useStore from '@store/index';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMeasurabilities } from '@api/get-measurabilities';
import { useGetPosts } from '@api/get-posts';
import { ModalSendCertificate } from '@components/v2/ui/ModalSendCertificate';
import { TableLastProductions } from '@components/v2/ui/TableLastProductions/TableLastProductions';
import { queryKeys } from '@config/constants/query-keys';
import { useBrandKpis } from '@features/dashboard/api/brand-kpis';
import { useProductions } from '@features/dashboard/api/get-productions';
import { useProducerKpis } from '@features/dashboard/api/producer-kpis';
import { useProjectKpis } from '@features/dashboard/api/project-kpis';

import { useGetAllAdditionalities } from '@api/get-all-additionalities';
import { useGetBrands } from '@api/get-brands2';
import { useWhoAmI } from '@api/get-whoami';
import { ACLS } from '@config/constants/acls';
import { useProjectKpisForDonutChartForDonutChart } from '@features/dashboard/api/project-kpis-for-donut-chart';
import { NewsSection } from '@features/dashboard/components/NewsSection';
import { NewsCardSkeleton } from '@features/dashboard/components/NewsSection/NewsSection';
import { useSendCertificate } from '@features/offsets/api/send-certificate';
import { sendCertificate } from '@features/offsets/api/send-certificate-asynchrenous';
import { TOffsetPopulated } from '@features/offsets/types';
import { EPostStatus } from '@global-types/api/enum';
import { TCertificate } from '@global-types/index';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { queryClient } from '@lib/react-query';
import { Carousel } from '@mantine/carousel';
import { useDisclosure } from '@mantine/hooks';
import { checkAclFront } from '@utils/acl-front';
import {
	convertArrayToLabelValue,
	convertGCO2EToTCO2EWithoutUnitFloor,
	truncateString,
} from '@utils/functions';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { useNavigate } from 'react-router'; // Api

type TQueryParamsProducer = {
	kpis: string[];
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Dashboard = () => {
	/* ******************************** HOOKS ******************************* */

	const { t, i18n } = useTranslation();
	const { checkAccess, checkRole, role } =
		useAuthorization();

	const { data: posts, isPending: isPendingGetPosts } =
		useGetPosts({
			params: {
				page: 1,
				pageSize: 4,
				status: EPostStatus.PUBLISHED,
				populate: `${
					checkAccess({
						allowedRoles: [
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						],
					})
						? 'createdBy,'
						: ''
				}cover`,
			},
		});

	const {
		getUser,
		setOffsetsSelectedRow,
		offsetsSelectedRow,
	} = useStore();

	const user = getUser();

	const { data: cachedUser, isPending: isPendingWhoAmI } =
		useWhoAmI();

	const producerRole = cachedUser?.roles.find((r) =>
		['PRODUCER_ADMIN', 'PRODUCER_STAFF'].includes(r.role)
	);

	const brandRole = cachedUser?.roles.find((r) =>
		['BRAND_ADMIN', 'BRAND_STAFF'].includes(r.role)
	);

	const entityId = producerRole?.entity ?? null;

	const entityBrandId = brandRole?.entity ?? null;

	const { mutate } = useCreateCertificate();
	/* ******************************** STATES ******************************* */

	const navigate = useNavigate();

	const [queryParamsDates, setQueryParamsDates] =
		useState<any>({});
	const [queryParams, setQueryParams] = useState<any>({
		...(entityBrandId ? { ids: [entityBrandId] } : {}),
		kpis: [
			'SUM_OFFSET_AMOUNT_GCO2',
			'SUM_OFFSET_AMOUNT_EURO',
		],
	});

	const [queryParamsProducer, setQueryParamsProducer] =
		useState<TQueryParamsProducer>({
			kpis: ['SUM_PLEDGED_ADDI_RECEIVED'],
			...(entityId && producerRole?.entity
				? { ids: [entityId] }
				: {}),
		});

	const [queryParamsProjects, setQueryParamsProjects] =
		useState<any>({
			...(checkRole([
				ROLES.ORGANIZATION_ADMIN,
				ROLES.PRODUCER_ADMIN,
				ROLES.PRODUCER_STAFF,
			])
				? {
						startDate: dayjs().startOf('year'),
						endDate: dayjs().endOf('year'),
					}
				: {}),

			kpis: [
				'SUM_OFFSET_AMOUNT_GCO2',
				'SUM_OFFSET_AMOUNT_EURO',
			],
		});

	const {
		data: offsets,
		isPending: isPendingGetLatestOffsets,
	} = useLatestOffsetsQuery({
		queryParams: {
			pageSize: 6,
		},
		sortParams: '-createdAt',
		queryConfig: {
			enabled: checkRole([
				ROLES.ADMIN,
				ROLES.SUPERADMIN,
				ROLES.STAFF,
				ROLES.USER,
				ROLES.ORGANIZATION_MEMBER,
				ROLES.ORGANIZATION_STAFF,
				ROLES.BRAND_ADMIN,
				ROLES.BRAND_STAFF,
			]),
		},
	});

	const handleDownloadCertificateApi = async (
		id: string,
		nameOffset: string,
		nameBrand: string,
		contributionDate: string,
		certificateId: string,
		lang: string,
		certificates: TCertificate[]
	) => {
		const idNotification = notifications.show({
			loading: true,
			title: `${t(
				`offsets.notifications.loadingTheCertificate`
			)} ${truncateString(nameOffset, 18)}`,
			message: `${t(
				`offsets.notifications.theDownloadOfCertification`
			)} ${nameOffset} ${t(
				`offsets.notifications.isCurrentlyInProgress`
			)}`,
			autoClose: false,
			withCloseButton: false,
		});

		const parsedContributionDate = dayjs(
			contributionDate,
			'DD/MM/YYYY'
		).format('DD-MM-YYYY');

		let offsetHasCertificate: boolean = false;

		if (certificates && Array.isArray(certificates)) {
			offsetHasCertificate =
				findIndex(certificates, {
					language: lang,
				}) !== -1;
		}

		if (offsetHasCertificate) {
			await getCertificate(
				certificateId,
				nameOffset,
				nameBrand,
				parsedContributionDate,
				idNotification
			);
		} else {
			mutate(
				{
					offset: id,
					language: lang,
				},
				{
					onSuccess: async (response) => {
						await getCertificate(
							response.id,
							nameOffset,
							nameBrand,
							parsedContributionDate,
							idNotification
						);

						queryClient.invalidateQueries({
							queryKey: [
								queryKeys.app.dashboard.latestOffsets.key,
							],
						});
					},
				}
			);
		}
	};

	const handleDownloadCertificate = async (data: any) => {
		const { certificates } = data;
		const foundCertificate = certificates.find(
			(item: any) => item.language == i18n.language
		);

		await handleDownloadCertificateApi(
			data.id,
			data.name,
			data.entity.name,
			data.contributionDate,
			foundCertificate?.id || '',
			i18n.language,
			data.certificates
		);
	};

	const {
		data: statisticsBrandKpis,
		mutate: statisticsBrandKpisMutation,
		isPending: statisticsBrandKpisIsPending,
	} = useBrandKpis({
		mutationConfig: {},
	});

	const {
		data: statisticsProjectKpis,
		mutate: statisticsProjectKpisMutation,
		isPending: statisticsProjectKpisIsPending,
	} = useProjectKpis({
		mutationConfig: {},
	});

	const {
		data: statisticsProjectKpisForDonutChart,
		mutate: statisticsProjectKpisForDonutChartMutation,
		isPending:
			statisticsProjectKpisForDonutChartIsPending,
	} = useProjectKpisForDonutChartForDonutChart({
		mutationConfig: {},
	});

	const {
		data: statisticsProducerKpis,
		mutate: statisticsProducerKpisMutation,
	} = useProducerKpis({
		mutationConfig: {},
	});

	const { data: measurabilities } = useGetMeasurabilities(
		{
			params: {},
			queryConfig: {
				enabled: !checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.BRAND_ADMIN,
					ROLES.BRAND_STAFF,
				]),
			},
		}
	);

	const { data: brands } = useGetBrands({
		params: {
			pageSize: 1000,
			sort: 'name',
		},
		queryConfig: {
			enabled: checkRole([
				ROLES.ADMIN,
				ROLES.SUPERADMIN,
				ROLES.STAFF,
				ROLES.BRAND_ADMIN,
				ROLES.BRAND_STAFF,
			]),
		},
	});

	const { data: additionalities } =
		useGetAllAdditionalities({
			params: {
				pageSize: 1000,
			},
			queryConfig: {
				enabled: checkRole([
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
				]),
			},
		});

	const [opened, { open, close: closeSendCertifiate }] =
		useDisclosure(false);

	const { isLoading: isLoadingCreateCertificate } =
		useCreateCertificate();

	const {
		mutate: mutateSendCertificate,
		isLoading: isLoadingSendCertificate,
	} = useSendCertificate();

	const handleSendCertificate = async (data: {
		other?: string;
		recepient: string | null;
		language: string;
	}) => {
		let offsetHasCertificate: boolean = false;

		if (
			offsetsSelectedRow.certificates &&
			Array.isArray(offsetsSelectedRow.certificates)
		) {
			offsetHasCertificate =
				findIndex(offsetsSelectedRow.certificates, {
					language: data.language,
				}) !== -1;
		}

		if (offsetHasCertificate) {
			const certificateFound =
				offsetsSelectedRow.certificates.find(
					(item: TCertificate) =>
						item.language == data.language
				);
			mutateSendCertificate(
				{
					certificateId: certificateFound.id,
					...(data &&
						!isNil(data.recepient) &&
						data.recepient !== 'other' && {
							email: data.recepient,
						}),

					...(data &&
						!isNil(data.recepient) &&
						data.recepient == 'other' &&
						!isNil(data.other) && { email: data.other }),
				},
				{
					onSuccess: async () => {
						closeSendCertifiate();
					},
				}
			);
		} else {
			mutate(
				{
					offset: offsetsSelectedRow.id,
					language: data.language,
				},
				{
					onSuccess: async (response) => {
						const idNotification = notifications.show({
							loading: true,
							title: t('global.In progress'),
							message: t(
								`notifications.certificateInProgress`
							),
							color: 'var(--mantine-color-igluGreen-9)',
							autoClose: false,
							withCloseButton: false,
						});
						const certificateId = response.id;

						await sendCertificate({
							idNotification,
							certificateId: certificateId,
							...(data &&
								!isNil(data.recepient) &&
								data.recepient !== 'other' && {
									email: data.recepient,
								}),

							...(data &&
								!isNil(data.recepient) &&
								data.recepient == 'other' &&
								!isNil(data.other) && {
									email: data.other,
								}),
						});
						handleCloseModalSendCertificate();
					},
				}
			);
		}
	};

	const getRecipientOptions = (
		offsetsSelectedRow: TOffsetPopulated,
		user: any
	) => {
		const options = [];

		const IS_ADMIN = checkRole([
			ROLES.ADMIN,
			ROLES.SUPERADMIN,
			ROLES.STAFF,
		]);

		const ownerEmail = get(
			offsetsSelectedRow,
			'organizationData.owner.email'
		);

		if (ownerEmail && IS_ADMIN) {
			options.push({
				label:
					`(${t('global.organizationOwner')}) ` +
					ownerEmail,
				value: ownerEmail,
			});
		}

		if (typeof user !== 'boolean' && user?.email) {
			options.push({
				label: `(${t('global.you')}) ` + user.email,
				value: user.email,
			});
		}

		const contactEmail = get(
			offsetsSelectedRow,
			'entity.contact.email'
		);
		if (contactEmail && IS_ADMIN) {
			options.push({
				label:
					`(${t('global.brandContact')}) ` + contactEmail,
				value: contactEmail,
			});
		}
		const uniqueOptions = options.filter(
			(item, index, self) =>
				self.findIndex(
					(el) => el.value === item.value
				) === index
		);

		return uniqueOptions;
	};

	const handleCloseModalSendCertificate = () => {
		closeSendCertifiate();
		setOffsetsSelectedRow({});
	};

	const [projectDataForChart, setProjectDataForChart] =
		useState<any>([]);
	const [
		projectDataEuroForChart,
		setProjectDataEuroForChart,
	] = useState<any>([]);

	useEffect(() => {
		if (checkRole([ROLES.USER])) {
			return;
		}

		if (
			checkRole([
				ROLES.ADMIN,
				ROLES.SUPERADMIN,
				ROLES.STAFF,
				ROLES.BRAND_ADMIN,
				ROLES.BRAND_STAFF,
			])
		) {
			statisticsBrandKpisMutation(queryParams);
		} else {
			statisticsProducerKpisMutation(queryParamsProducer);
		}

		if (
			!checkRole([ROLES.BRAND_ADMIN, ROLES.BRAND_STAFF])
		) {
			statisticsProjectKpisMutation(queryParams);
			statisticsProjectKpisForDonutChartMutation(
				queryParams
			);
		}
	}, []);

	const [selectedDates, setSelectedDates] =
		useState<any>(null);

	const [selectedUnit, setSelectedUnit] = useState('€');

	useEffect(() => {
		if (
			statisticsProjectKpisForDonutChart &&
			statisticsProjectKpisForDonutChart.kpis &&
			statisticsProjectKpisForDonutChart.projects &&
			statisticsProjectKpisForDonutChart.projects.length >
				0
		) {
			const totalGCO2 =
				statisticsProjectKpisForDonutChart.kpis
					.SUM_OFFSET_AMOUNT_GCO2;

			const totalEuro =
				statisticsProjectKpisForDonutChart.kpis
					.SUM_OFFSET_AMOUNT_EURO;

			const data =
				statisticsProjectKpisForDonutChart.projects.map(
					(proj: any) => {
						const valuePercent =
							(proj.kpis.SUM_OFFSET_AMOUNT_GCO2 /
								totalGCO2) *
							100;

						return {
							name: proj.name,
							value: +valuePercent.toFixed(2),
							color: '#F0F0F0',
						};
					}
				);

			const dataEuro =
				statisticsProjectKpisForDonutChart.projects.map(
					(proj: any) => {
						const valuePercent =
							(proj.kpis?.SUM_OFFSET_AMOUNT_EURO /
								totalEuro) *
							100;

						return {
							name: proj.name,
							value: +valuePercent.toFixed(2),
							color: '#F0F0F0',
						};
					}
				);

			setProjectDataForChart(data);
			setProjectDataEuroForChart(dataEuro);
		}
	}, [statisticsProjectKpisForDonutChart]);

	const {
		data: productions,
		isLoading: isPendingGetLastProductions,
	} = useProductions(1, 5);

	const hasPosts =
		Array.isArray(posts?.rows) && posts.rows.length > 0;

	/* ******************************** RENDERING ******************************* */
	return (
		<Box w="100%">
			<ModalSendCertificate
				loading={
					isLoadingSendCertificate ||
					isLoadingCreateCertificate
				}
				opened={opened}
				recepientOptions={getRecipientOptions(
					offsetsSelectedRow,
					user
				)}
				handlesubmit={(data: any) => {
					handleSendCertificate(data);
				}}
				handleclose={handleCloseModalSendCertificate}
			/>
			<KpiSection
				isLoading={
					statisticsProjectKpisIsPending ||
					statisticsBrandKpisIsPending
				}
				data={[
					...(checkAclFront(
						ACLS.FRONT.DASHBOARD
							.KPI_COMPENSATED_EMISSIONS,
						role as ROLES
					)
						? [
								{
									image:
										'https://res.cloudinary.com/inuk/image/upload/v1750080711/inuk/IGLU/ea8cib79lsvijobuwxvz.jpg',
									label: checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
										ROLES.BRAND_ADMIN,
										ROLES.BRAND_STAFF,
									])
										? t('global.compensatedEmissions')
										: t('global.carbonCreditsSold'),
									value: checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
										ROLES.BRAND_ADMIN,
										ROLES.BRAND_STAFF,
									])
										? convertGCO2EToTCO2EWithoutUnitFloor(
												statisticsBrandKpis?.kpis
													?.SUM_OFFSET_AMOUNT_GCO2 || 0
											)
										: convertGCO2EToTCO2EWithoutUnitFloor(
												statisticsProjectKpis?.kpis
													?.SUM_OFFSET_AMOUNT_GCO2 || 0
											),
									unit: 'tCO2e',
									dateRange: `${
										selectedDates &&
										!isNil(selectedDates[0])
											? selectedDates[0]
											: checkRole([
														ROLES.ADMIN,
														ROLES.SUPERADMIN,
														ROLES.STAFF,
														ROLES.BRAND_ADMIN,
														ROLES.BRAND_STAFF,
												  ])
												? dayjs(
														statisticsBrandKpis?.startDate
													).format('YYYY-MM-DD')
												: dayjs(
														statisticsProjectKpis?.startDate
													).format('YYYY-MM-DD')
									} - ${
										selectedDates &&
										!isNil(selectedDates[1])
											? selectedDates[1]
											: checkRole([
														ROLES.ADMIN,
														ROLES.SUPERADMIN,
														ROLES.STAFF,
														ROLES.BRAND_ADMIN,
														ROLES.BRAND_STAFF,
												  ])
												? dayjs(
														statisticsBrandKpis?.endDate
													).format('YYYY-MM-DD')
												: dayjs(
														statisticsProjectKpis?.endDate
													).format('YYYY-MM-DD')
									}`,
									color:
										'var(--mantine-color-igluBlue-9)',
								},
							]
						: []),
					...(!checkRole([
						ROLES.PRODUCER_ADMIN,
						ROLES.PRODUCER_STAFF,
						ROLES.STAFF,
					]) &&
					checkAclFront(
						ACLS.FRONT.DASHBOARD.KPI_TOTAL_COMPONSATION,
						role as ROLES
					)
						? [
								{
									image:
										'https://res.cloudinary.com/inuk/image/upload/v1750080753/inuk/IGLU/ne09tnligsveqfmvpik3.jpg',
									label: checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.BRAND_ADMIN,
										ROLES.BRAND_STAFF,
									])
										? t('global.totalCompensation')
										: t('global.revenueGenerated'),
									value: checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.BRAND_ADMIN,
										ROLES.BRAND_STAFF,
									])
										? Math.floor(
												statisticsBrandKpis?.kpis
													?.SUM_OFFSET_AMOUNT_EURO || 0
											)
										: Math.floor(
												statisticsProjectKpis?.kpis
													?.SUM_OFFSET_AMOUNT_EURO || 0
											),
									unit: '€',
									dateRange: `${
										selectedDates &&
										!isNil(selectedDates[0])
											? selectedDates[0]
											: checkRole([
														ROLES.ADMIN,
														ROLES.SUPERADMIN,
														ROLES.BRAND_ADMIN,
														ROLES.BRAND_STAFF,
												  ])
												? dayjs(
														statisticsBrandKpis?.startDate
													).format('YYYY-MM-DD')
												: dayjs(
														statisticsProjectKpis?.startDate
													).format('YYYY-MM-DD')
									} - ${
										selectedDates &&
										!isNil(selectedDates[1])
											? selectedDates[1]
											: checkRole([
														ROLES.ADMIN,
														ROLES.SUPERADMIN,
														ROLES.BRAND_ADMIN,
														ROLES.BRAND_STAFF,
												  ])
												? dayjs(
														statisticsBrandKpis?.endDate
													).format('YYYY-MM-DD')
												: dayjs(
														statisticsProjectKpis?.endDate
													).format('YYYY-MM-DD')
									}`,
									color:
										'var(--mantine-color-igluGreen-9)',
								},
							]
						: []),
					...((checkAclFront(
						ACLS.FRONT.DASHBOARD
							.KPI_FINANCING_OF_ADDITIONALITY,
						role as ROLES
					) &&
						checkRole([
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						]) &&
						Array.isArray(additionalities?.rows) &&
						additionalities?.rows.length > 0) ||
					(checkAclFront(
						ACLS.FRONT.DASHBOARD
							.KPI_FINANCING_OF_ADDITIONALITY,
						role as ROLES
					) &&
						!checkRole([
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
							ROLES.STAFF,
						]))
						? [
								{
									image:
										'https://res.cloudinary.com/inuk/image/upload/v1750080784/inuk/IGLU/idjcwq9w5a8njxihojf0.jpg',
									label: checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
										ROLES.BRAND_ADMIN,
										ROLES.BRAND_STAFF,
									])
										? t(
												'global.financementAdditionnalite'
											)
										: t(
												'global.fundingGeneratedForAdditionalProjects'
											),
									value: checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
										ROLES.BRAND_ADMIN,
										ROLES.BRAND_STAFF,
									])
										? Math.floor(
												(statisticsBrandKpis?.kpis
													?.SUM_OFFSET_AMOUNT_EURO || 0) *
													0.4
											)
										: Math.floor(
												statisticsProducerKpis?.kpis
													?.SUM_PLEDGED_ADDI_RECEIVED || 0
											),
									unit: '€',
									dateRange: `${
										selectedDates &&
										!isNil(selectedDates[0])
											? selectedDates[0]
											: checkRole([
														ROLES.ADMIN,
														ROLES.SUPERADMIN,
														ROLES.STAFF,
														ROLES.BRAND_ADMIN,
														ROLES.BRAND_STAFF,
												  ])
												? dayjs(
														statisticsBrandKpis?.startDate
													).format('YYYY-MM-DD')
												: dayjs(
														statisticsProducerKpis?.startDate
													).format('YYYY-MM-DD')
									} - ${
										selectedDates &&
										!isNil(selectedDates[1])
											? selectedDates[1]
											: checkRole([
														ROLES.ADMIN,
														ROLES.SUPERADMIN,
														ROLES.STAFF,
														ROLES.BRAND_ADMIN,
														ROLES.BRAND_STAFF,
												  ])
												? dayjs(
														statisticsBrandKpis?.endDate
													).format('YYYY-MM-DD')
												: dayjs(
														statisticsProducerKpis?.endDate
													).format('YYYY-MM-DD')
									}`,
									color:
										'var(--mantine-color-igluYellow-9)',
								},
							]
						: []),
				]}
				companyOptions={convertArrayToLabelValue(
					brands?.rows
				)}
				onCompanyChange={(option) => {
					let body = {};
					if (isNil(option)) {
						body = {
							kpis: [
								'SUM_OFFSET_AMOUNT_GCO2',
								'SUM_OFFSET_AMOUNT_EURO',
							],
						};
						setQueryParams(body);
					} else {
						body = {
							...queryParams,
							ids: [option],
						};
						setQueryParams(body);
					}

					statisticsBrandKpisMutation(body);
				}}
				projectOptions={convertArrayToLabelValue(
					measurabilities?.rows &&
						Array.isArray(measurabilities?.rows)
						? measurabilities?.rows
						: []
				)}
				onProjectChange={(option) => {
					let body = {};
					if (isNil(option)) {
						body = {
							kpis: [
								'SUM_OFFSET_AMOUNT_GCO2',
								'SUM_OFFSET_AMOUNT_EURO',
							],
						};
						setQueryParamsProjects(body);
					} else {
						body = {
							...queryParams,
							ids: [option],
						};
						setQueryParamsProjects(body);
					}

					statisticsProjectKpisMutation(body);
				}}
				onDateChange={(value) => {
					const [rawStartDate, rawEndDate] = value;

					// Corriger l'ordre si besoin
					let startDate = rawStartDate;
					let endDate = rawEndDate;

					if (
						startDate &&
						endDate &&
						dayjs(startDate).isAfter(dayjs(endDate))
					) {
						// Inverser si start > end
						[startDate, endDate] = [endDate, startDate];
					}

					const {
						startDate: _,
						endDate: __,
						...restQueryParams
					} = queryParams;

					const body = {
						...restQueryParams,
						...(startDate ? { startDate } : {}),
						...(endDate ? { endDate } : {}),
					};

					setQueryParams(body);

					if (
						checkRole([
							ROLES.ORGANIZATION_ADMIN,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						])
					) {
						statisticsProjectKpisMutation(body);
					} else {
						statisticsBrandKpisMutation(body);
					}

					const updatedProducerParams = {
						...queryParamsProducer,
					};

					setQueryParamsProducer(updatedProducerParams);

					setQueryParamsDates({
						...(startDate ? { startDate } : {}),
						...(endDate ? { endDate } : {}),
					});

					setSelectedDates([startDate, endDate]);
				}}
			/>
			<br />
			<Flex
				direction={{
					base: 'column',
					xs: 'column',
					sm: 'column',
					md: 'column',
					xl: 'row',
				}}
				gap={{ base: 'sm', sm: 'lg' }}
			>
				{Array.isArray(
					statisticsProjectKpisForDonutChart?.projects
				) &&
					statisticsProjectKpisForDonutChart.projects
						.length > 1 &&
					checkAclFront(
						ACLS.FRONT.DASHBOARD.DONUTCHART,
						role as ROLES
					) && (
						<DonutChartContributions
							onlyTco2={false}
							title={t('global.contributionBreakdown')}
							onViewMore={() => {
								navigate('/measurability-projects');
							}}
							data={
								checkRole([ROLES.STAFF])
									? projectDataForChart
									: selectedUnit === '€'
										? projectDataEuroForChart
										: projectDataForChart
							}
							isLoading={
								statisticsProjectKpisForDonutChartIsPending
							}
							onChangeUnit={(unit: string) => {
								setSelectedUnit(unit);
							}}
							options={
								checkRole([ROLES.STAFF])
									? ['tCO2e']
									: ['€', 'tCO2e']
							}
						/>
					)}
				{checkAclFront(
					ACLS.FRONT.DASHBOARD.LAST_OFFSETS_LIST,
					role as ROLES
				) && (
					<Authorization
						allowedRoles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.USER,
							ROLES.ORGANIZATION_MEMBER,
							ROLES.ORGANIZATION_STAFF,
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
						]}
					>
						<TableLastContributions
							onSeeMoreClick={() => {
								navigate('/offsets');
							}}
							data={offsets}
							isLoading={isPendingGetLatestOffsets}
							onDownload={handleDownloadCertificate}
							onSend={(offset) => {
								setOffsetsSelectedRow(offset);
								open();
							}}
						/>
					</Authorization>
				)}
				{checkAclFront(
					ACLS.FRONT.DASHBOARD.LAST_PRODUCTIONS_LIST,
					role as ROLES
				) && (
					<Authorization
						allowedRoles={[
							ROLES.ORGANIZATION_ADMIN,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						]}
					>
						<TableLastProductions
							onSeeMoreClick={() => {
								navigate('/productions');
							}}
							data={productions}
							isLoading={isPendingGetLastProductions}
						/>
					</Authorization>
				)}
			</Flex>
			<br />
			{checkAclFront(
				ACLS.FRONT.DASHBOARD.LAST_NEWS,
				role as ROLES
			) &&
				checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.BRAND_ADMIN,
					ROLES.BRAND_STAFF,
				]) &&
				isPendingGetPosts && (
					<Carousel
						withIndicators={false}
						slideSize={{
							base: '100%',
							sm: '50%',
							md: '25%',
						}}
						emblaOptions={{ loop: true, align: 'start' }}
						slideGap="8px"
						p="0px"
						m="sm"
					>
						{Array.from({ length: 5 }).map((_, i) => (
							<Carousel.Slide key={i}>
								<NewsCardSkeleton />
							</Carousel.Slide>
						))}
					</Carousel>
				)}
			{checkAclFront(
				ACLS.FRONT.DASHBOARD.LAST_NEWS,
				role as ROLES
			) &&
				checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.BRAND_ADMIN,
					ROLES.BRAND_STAFF,
				]) &&
				!isPendingGetPosts &&
				hasPosts && (
					<NewsSection
						posts={posts.rows}
						isPendingGetPosts={isPendingGetPosts}
					/>
				)}
		</Box>
	);
};

export default Dashboard;
